import { useEffect, useRef } from "react";

type HasStatus = { status?: string | null };

export type PaidStatusChecker = (status?: string | null) => boolean;
export type StatusFetcher<T extends HasStatus> = (id: string) => Promise<T>; // faça a função lançar erro nos casos de falha

export type UsePixPollingOptions<T extends HasStatus> = {
	active: boolean;
	transactionId?: string | null;
	statusFetcher: StatusFetcher<T>;
	intervalMs?: number; // padrão 30s
	maxAttempts?: number; // padrão 5
	onPaid?: (payload: T) => void;
	isPaidStatus?: PaidStatusChecker;
	pauseWhenHidden?: boolean; // padrão true (economiza servidor)
};

const defaultIsPaidStatus: PaidStatusChecker = (status) => {
	if (!status) return false;
	const s = String(status).toLowerCase();
	return ["paid", "approved", "completed", "confirmed", "success"].includes(s);
};

export function usePixPolling<T extends HasStatus>({
	active,
	transactionId,
	statusFetcher,
	intervalMs = 30_000,
	maxAttempts = 5,
	onPaid,
	isPaidStatus = defaultIsPaidStatus,
	pauseWhenHidden = true,
}: UsePixPollingOptions<T>) {
	const attemptsRef = useRef(0);
	const inFlightRef = useRef(false);
	const onPaidRef = useRef(onPaid);
	onPaidRef.current = onPaid;

	const intervalIdRef = useRef<number | null>(null);
	const stoppedRef = useRef(false);

	useEffect(() => {
		if (!active || !transactionId) return;

		attemptsRef.current = 0;
		stoppedRef.current = false;

		const stopNow = () => {
			stoppedRef.current = true;
			if (intervalIdRef.current !== null) {
				window.clearInterval(intervalIdRef.current);
				intervalIdRef.current = null;
			}
		};

		const check = async () => {
			if (stoppedRef.current || inFlightRef.current) return;

			if (attemptsRef.current >= maxAttempts) {
				stopNow();
				return;
			}

			if (pauseWhenHidden && typeof document !== "undefined" && document.hidden) {
				// pausa sem contar tentativa
				return;
			}

			inFlightRef.current = true;
			attemptsRef.current += 1;

			try {
				const res = await statusFetcher(transactionId);
				if (isPaidStatus(res?.status)) {
					stopNow();
					onPaidRef.current?.(res);
					return;
				}
			} catch {
				// falha de rede/erro da função -> ignora e segue até o limite
			} finally {
				inFlightRef.current = false;
			}
		};

		// 1ª checagem imediata
		void check();

		// Próximas checagens
		intervalIdRef.current = window.setInterval(() => {
			if (stoppedRef.current) {
				stopNow();
				return;
			}
			void check();
		}, intervalMs);

		return () => {
			stopNow();
		};
	}, [active, transactionId, statusFetcher, intervalMs, maxAttempts, isPaidStatus, pauseWhenHidden]);
}
